import './styles.scss';

import React, { useCallback, useContext, useEffect, useState } from 'react';

import {
    useContactFriendStrings,
    useFormFields,
    useStoryblokStrings,
} from '../../hooks';
import { ApiContext } from '../../providers';
import {
    ContactFormHeading,
    FormInput,
    FormLabel,
    FormTextarea,
} from './styles';
import { Button } from '../Button';
import { Loader } from '../Loader';

export type ContactFormFormType = 'tellFriend' | 'challengeFriend';

interface ContactFormProps {
    formType: ContactFormFormType;
}

export const ContactForm = ({ formType }: ContactFormProps) => {
    const { Upscore } = useContext(ApiContext);
    const formStrings = useStoryblokStrings('form-strings');

    const {
        data: { title, message, response },
    } = useContactFriendStrings(formType);

    const [isLoading, setIsLoading] = useState(false);
    const [showResponse, setShowResponse] = useState(false);

    const [fieldsValues, setFieldsValues] = useFormFields([]);

    const handleFieldChange = useCallback(
        (event) => {
            const { value, name } = event.target;

            setFieldsValues({
                ...fieldsValues,
                [name]: value,
            });
        },
        [fieldsValues, setFieldsValues],
    );

    const handleSubmitForm = useCallback(
        async (e) => {
            e.preventDefault();
            setIsLoading(true);

            const { email, message: fieldMessage } = fieldsValues;

            try {
                if (formType === 'tellFriend') {
                    await Upscore.postUserTips({
                        email,
                        message: fieldMessage,
                    });
                } else {
                    await Upscore.postUserChallenge({
                        email,
                        message: fieldMessage,
                    });
                }

                setShowResponse(true);
            } catch (error) {
                console.error(error);
            }

            setIsLoading(false);
        },
        [fieldsValues, formType],
    );

    useEffect(() => {
        setFieldsValues({
            message,
        });
    }, [formType, message, setFieldsValues]);

    if (isLoading) {
        return <Loader />;
    }

    if (showResponse) {
        return <h3 className="ContactFriend__title">{response}</h3>;
    }

    return (
        <form onSubmit={handleSubmitForm} className="ContactFriend">
            {title && <ContactFormHeading>{title}</ContactFormHeading>}
            <FormLabel>
                <FormInput
                    type="email"
                    name="email"
                    placeholder={formStrings.field_email || ''}
                    value={fieldsValues.email || ''}
                    onChange={handleFieldChange}
                    required
                />
            </FormLabel>

            <FormTextarea
                name="message"
                value={fieldsValues.message || ''}
                onChange={handleFieldChange}
                required
            />

            <Button variant="primary" type="submit">
                {formStrings.submit || 'Submit'}
            </Button>
        </form>
    );
};
